// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { ReactNode, useEffect } from 'react'
import { Link } from 'react-router-dom'

// ============================================================================
// Legal / compliance pages — Privacy Policy, Terms of Use, Data Rights,
// Accessibility Statement.
// ============================================================================

const LAST_UPDATED = 'June 25, 2026'
// Stable version key for acceptance capture (PR-B2). Bump this string whenever the legal text
// changes materially; the accepted version is recorded against it, not against the display prose.
// Keep in sync across documents unless they version independently.
export const POLICY_VERSION = '2026-05-30'


const pageWrap: React.CSSProperties = {
  maxWidth: '100%', margin: '0 auto', padding: '32px 24px 64px',
}
const h1: React.CSSProperties = { fontSize: 28, fontWeight: 700, color: 'var(--color-text)', marginBottom: 4 }
const h2: React.CSSProperties = { fontSize: 17, fontWeight: 600, color: 'var(--color-text)', marginTop: 28, marginBottom: 8 }
const p: React.CSSProperties = { fontSize: 14, lineHeight: 1.7, color: 'var(--color-text)', marginBottom: 12 }
const meta: React.CSSProperties = { fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 24 }

function LegalShell({ title, children }: { title: string; children: ReactNode }) {
  useEffect(() => { document.title = `${title} — OwlRx` }, [title])
  return (
    <main style={pageWrap}>
      <h1 style={h1}>{title}</h1>
      <div style={meta}>Last updated {LAST_UPDATED}</div>
      {children}
      <div style={{ marginTop: 40, paddingTop: 16, borderTop: '1px solid var(--color-border)', fontSize: 13 }}>
        <Link to="/legal/privacy">Privacy Policy</Link>{' · '}
        <Link to="/legal/terms">Terms of Use</Link>{' · '}
        <Link to="/legal/data-rights">Your Data Rights</Link>
      </div>
    </main>
  )
}

export function PrivacyPolicyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <p style={p}>
        This Privacy Policy describes how this pharmacy management platform ("the Service") collects,
        uses, discloses, and safeguards information, including protected health information ("PHI") as
        defined under the Health Insurance Portability and Accountability Act of 1996 ("HIPAA").
      </p>

      <h2 style={h2}>Information we collect</h2>
      <p style={p}>
        The Service processes information necessary to support pharmacy operations, including patient
        demographic data, prescription and medication records, prescriber information, insurance and
        billing details, and shipment and delivery records. Access credentials and audit logs are also
        maintained for security and accountability.
      </p>

      <h2 style={h2}>How we use information</h2>
      <p style={p}>
        Information is used to provide pharmacy workflow, dispensing, shipment tracking, and related
        services; to maintain security and integrity of the Service; to comply with legal and
        regulatory obligations; and for treatment, payment, and healthcare operations as permitted by
        HIPAA. We do not sell personal or health information, and the Service does not display
        third-party advertising.
      </p>

      <h2 style={h2}>Disclosure of information</h2>
      <p style={p}>
        PHI may be disclosed to the patient's healthcare providers, payers, and business associates
        bound by written agreements, and as otherwise required or permitted by law. Carriers and
        delivery partners receive only the minimum information necessary to complete a delivery.
      </p>

      <h2 style={h2}>Safeguards</h2>
      <p style={p}>
        The Service applies administrative, physical, and technical safeguards, including role-based
        access controls, authentication, encryption of sensitive data, and audit logging, consistent
        with the HIPAA Security Rule. Access to records is restricted to authorized users of the
        applicable pharmacy organization.
      </p>

      <h2 style={h2}>Data retention</h2>
      <p style={p}>
        Records are retained for the period required by applicable pharmacy, state, and federal law,
        after which they are securely disposed of or de-identified.
      </p>

      <h2 style={h2}>Contact</h2>
      <p style={p}>
        Questions about this policy or about privacy practices may be directed to the pharmacy's
        designated Privacy Officer.
      </p>
    </LegalShell>
  )
}

export function TermsOfUsePage() {
  return (
    <LegalShell title="Terms of Use">
      <p style={p}>
        These Terms of Use govern access to and use of the Service. By accessing the Service, the user
        agrees to these terms on behalf of their organization.
      </p>

      <h2 style={h2}>Authorized use</h2>
      <p style={p}>
        The Service is intended for use by licensed pharmacy personnel and authorized staff in the
        course of their duties. Users must access only the information they are permitted to access and
        must not attempt to circumvent access controls or tenant boundaries.
      </p>

      <h2 style={h2}>Account security</h2>
      <p style={p}>
        Users are responsible for maintaining the confidentiality of their credentials and for all
        activity under their account. Suspected unauthorized access must be reported promptly.
      </p>

      <h2 style={h2}>Acceptable use</h2>
      <p style={p}>
        Users may not use the Service to violate any law or regulation, to access or disclose PHI
        except as permitted, to introduce malicious code, or to interfere with the operation or
        security of the Service.
      </p>

      <h2 style={h2}>Accuracy of records</h2>
      <p style={p}>
        While the Service supports pharmacy workflow, clinical and dispensing decisions remain the
        responsibility of licensed professionals. Users should verify critical information against
        authoritative source systems.
      </p>

      <h2 style={h2}>Ownership and license</h2>
      <p style={p}>
        The Service, including all software, source code, design, documentation, trademarks, and other
        intellectual property, is owned by Owl Consultations and its licensors. All rights are
        reserved. Access is provided under a limited, non-exclusive, non-transferable, revocable
        license for the licensee organization's internal business use only. No right is granted to
        copy, modify, distribute, sublicense, resell, or create derivative works of the Service, or to
        reverse engineer, decompile, or disassemble any part of it except where such restriction is
        prohibited by law.
      </p>

      <h2 style={h2}>Unauthorized use</h2>
      <p style={p}>
        Access is limited to authorized users. Activity on the Service is logged and monitored.
        Unauthorized access or use — including exceeding authorized access, credential sharing, data
        scraping, or attempts to bypass security or tenant boundaries — is prohibited and may be
        referred for criminal prosecution under applicable law, including the Computer Fraud and Abuse
        Act (18 U.S.C. § 1030) and California Penal Code § 502, in addition to civil remedies,
        injunctive relief, and termination of access.
      </p>

      <h2 style={h2}>Disclaimer and limitation</h2>
      <p style={p}>
        The Service is provided "as is" without warranties of any kind to the extent permitted by law.
        These terms may be updated; continued use after changes constitutes acceptance.
      </p>

      <h2 style={h2}>Governing law</h2>
      <p style={p}>
        These terms are governed by the laws of the State of California, without regard to conflict of
        law principles, except where superseded by applicable federal law.
      </p>
    </LegalShell>
  )
}

export function DataRightsPage() {
  return (
    <LegalShell title="Your Data Rights">
      <p style={p}>
        Individuals have rights regarding their protected health information under HIPAA and may have
        additional rights under applicable state laws. This page summarizes those rights.
      </p>

      <h2 style={h2}>Right to access</h2>
      <p style={p}>
        Individuals may request to inspect and obtain a copy of their health information maintained in
        the designated record set, subject to limited exceptions.
      </p>

      <h2 style={h2}>Right to amend</h2>
      <p style={p}>
        Individuals may request correction of information they believe is inaccurate or incomplete.
        Requests are reviewed in accordance with HIPAA requirements.
      </p>

      <h2 style={h2}>Right to an accounting of disclosures</h2>
      <p style={p}>
        Individuals may request a list of certain disclosures of their health information.
      </p>

      <h2 style={h2}>Right to request restrictions</h2>
      <p style={p}>
        Individuals may request restrictions on certain uses and disclosures, and may request to
        receive communications by alternative means or at alternative locations.
      </p>

      <h2 style={h2}>Right to a paper copy and to file a complaint</h2>
      <p style={p}>
        Individuals may obtain a paper copy of the privacy notice and may file a complaint with the
        pharmacy's Privacy Officer or with the U.S. Department of Health and Human Services without fear
        of retaliation.
      </p>

      <h2 style={h2}>Deletion requests</h2>
      <p style={p}>
        You may request deletion of personal information that is not part of the pharmacy or medical
        record (for example, portal account and contact details). Pharmacy and medical records are
        subject to mandatory retention under federal and state law (including HIPAA documentation
        requirements, 45 C.F.R. § 164.530(j), and state pharmacy record-retention rules), and deletion
        requests for those records will receive a written response explaining the legal basis on which
        the records must be retained. Where deletion is not available, you retain your rights to
        access, amend, and restrict as described above.
      </p>

      <h2 style={h2}>Exercising your rights</h2>
      <p style={p}>
        To exercise any of these rights, contact the pharmacy's designated Privacy Officer. The pharmacy
        will respond within the timeframes required by applicable law.
      </p>
    </LegalShell>
  )
}

export function AccessibilityStatementPage() {
  return (
    <LegalShell title="Accessibility Statement">
      <p style={p}>
        Owl Consultations is committed to making this Service accessible to all users, including
        people with disabilities. Our target standard is the Web Content Accessibility Guidelines
        (WCAG) 2.1, Level AA — the standard adopted for health programs under Section 504 of the
        Rehabilitation Act and referenced by the Americans with Disabilities Act.
      </p>

      <h2 style={h2}>Measures we take</h2>
      <p style={p}>
        Accessibility checks run in our automated build pipeline on public pages; the interface
        provides keyboard access with a skip-to-content link and visible focus indicators, screen
        reader landmarks and announcements on navigation, labeled form fields with status messaging,
        and per-page titles. Manual audits with assistive technology are part of our ongoing
        remediation program.
      </p>

      <h2 style={h2}>Known limitations</h2>
      <p style={p}>
        This product is under active development. Some interior screens, data tables, and charts have
        not yet completed manual audit and may have gaps in keyboard or screen-reader support. We are
        remediating these on a scheduled basis.
      </p>

      <h2 style={h2}>Feedback</h2>
      <p style={p}>
        If you encounter an accessibility barrier, contact your pharmacy's support contact or the
        platform operator. Please describe the page and the assistive technology in use; we aim to
        respond promptly and to provide the information you need in an accessible format.
      </p>
    </LegalShell>
  )
}
